<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="AWS向けVNコネクト設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <cross-validation-observer
        :initial-values="{
          routeAggregationAddressList:
            form.cloudVnConnectCommon!.routeAggregationAddressList &&
            form.cloudVnConnectCommon!.routeAggregationAddressList.length > 0
              ? form.cloudVnConnectCommon!.routeAggregationAddressList
              : [''],
        }"
        v-slot="{ handleSubmit }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- クラウド設定 -->
                <v-row>
                  <v-col>
                    <h3>クラウド設定</h3>
                  </v-col>
                </v-row>
                <!-- サービスキー -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="AWSアカウントID"
                      msg="半角数字"
                      :is-info="isAdd"
                      :isRequired="isAdd"
                    />
                    <template v-if="isAdd">
                      <validation-provider
                        name="serviceKey"
                        label="AWSアカウントID"
                        v-model="
                          (form as CloudVnAwsPost).cloudVnConnectCommon!
                            .serviceKey
                        "
                        :rules="{ required: true, numeric: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <app-form-input
                      v-else
                      :value="cloudVnAwsDetail!.cloudVnConnectCommon.serviceKey"
                      disabled
                    />
                  </v-col>
                </v-row>
                <!--接続サービス-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="接続サービス"
                      msg="ホスト接続はクラウド帯域が50Mbps以上の場合に設定可能"
                      :is-info="isAdd && availableStatus"
                      :isRequired="isAdd && availableStatus"
                    />
                    <template v-if="isAdd && availableStatus">
                      <validation-provider
                        name="directConnectType"
                        label="接続サービス"
                        v-model="form.cloudVnConnectCommon!.directConnectType"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-select
                          :items="directConnectTypeSelectList"
                          item-title="text"
                          item-value="value"
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <span v-else class="ml-3">
                      {{
                        $filter.enumTo(
                          form.cloudVnConnectCommon!.directConnectType,
                          "awsConnectType"
                        )
                      }}
                    </span>
                  </v-col>
                </v-row>
                <!--ロケーション-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ロケーション"
                      :is-info="false"
                      :isRequired="isAdd"
                    />
                    <template v-if="isAdd">
                      <validation-provider
                        name="location"
                        label="ロケーション"
                        v-model="form.cloudVnConnectCommon!.location"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-select
                          :items="locationOptions"
                          item-title="text"
                          item-value="value"
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <app-form-input
                      v-else
                      :value="
                        $filter.enumTo(
                          cloudVnAwsDetail!.cloudVnConnectCommon.location,
                          'location'
                        )
                      "
                      disabled
                    />
                  </v-col>
                </v-row>
                <!--接続先仮想ネットワーク(VPN/VN)名-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="接続先仮想ネットワーク(VPN/VN)名"
                      :is-info="false"
                      :isRequired="isAdd"
                    />
                    <template v-if="isAdd">
                      <validation-provider
                        name="selectedVpnVn"
                        label="接続先仮想ネットワーク(VPN/VN)名"
                        v-model="selectedVpnVn"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-select-search
                          :items="vpnVnCodeSelectList"
                          item-title="text"
                          item-value="value"
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <!--変更の場合、変更不可-->
                    <app-form-input v-else :value="vpnVnName" disabled />
                  </v-col>
                </v-row>

                <!-- VNコネクト設定 -->
                <v-row>
                  <v-col>
                    <h3>VNコネクト設定</h3>
                  </v-col>
                </v-row>
                <!--品目-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="品目"
                      :msg="itemMessage"
                      :is-info="
                        isAdd &&
                        (isBandwidthItemShow ||
                          form.cloudVnConnectCommon!.directConnectType ===
                            'HOSTED_CONNECTIONS')
                      "
                      :isRequired="!isHostedConnections"
                    />
                    <template v-if="isAdd && isBandwidthItemShow">
                      <v-radio-group
                        v-model="form.bandItem"
                        hide-details
                        inline
                        density="comfortable"
                      >
                        <v-radio label="低帯域" value="LOW" />
                        <v-radio
                          label="広帯域"
                          value="HIGH"
                          :disabled="highBandwidthDisable"
                        />
                      </v-radio-group>
                    </template>
                    <template v-if="!isAdd && isBandwidthItemShow">
                      <span>{{ bandItemName }}</span>
                    </template>
                    <template v-if="!isHostedConnections">
                      <validation-provider
                        name="bandwidth"
                        label="品目"
                        v-model="form.cloudVnConnectCommon!.bandwidth"
                        :rules="{
                          required: true,
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-select
                          :items="bandwidthSelectList"
                          item-title="text"
                          item-value="value"
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <app-form-input
                      v-else
                      :value="
                        $filter.bandwidthTo(
                          form.cloudVnConnectCommon!.bandwidth
                        )
                      "
                      disabled
                    />
                  </v-col>
                </v-row>
                <!--VNコネクト名(Act)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="VNコネクト名(Act)"
                      :msg="
                        $filter.message('info_max_length', {
                          maxLength: '20',
                        })
                      "
                      :is-info="isVnConnectNameActEnable"
                      :isRequired="isVnConnectNameActEnable"
                    />
                    <validation-provider
                      name="vnConnectNameAct"
                      label="VNコネクト名(Act)"
                      v-model="form.cloudVnConnectAct!.vnConnectName"
                      :rules="
                        isVnConnectNameActEnable
                          ? {
                              required: true,
                              max: { length: 20 },
                              alpha_num_zenkaku: true,
                            }
                          : undefined
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="!isVnConnectNameActEnable"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--VNコネクト名(Sby)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="VNコネクト名(Sby)"
                      :msg="
                        $filter.message('info_max_length', {
                          maxLength: '20',
                        })
                      "
                      :is-info="isVnConnectNameSbyEnable"
                      :isRequired="
                        !!(
                          cloudVnAwsDetail && cloudVnAwsDetail.cloudVnConnectSby
                        ) && isVnConnectNameSbyEnable
                      "
                    />
                    <validation-provider
                      name="vnConnectNameSby"
                      label="VNコネクト名(Sby)"
                      v-model="form.cloudVnConnectSby!.vnConnectName"
                      :rules="
                        cloudVnAwsDetail &&
                        cloudVnAwsDetail.cloudVnConnectSby &&
                        isVnConnectNameSbyEnable
                          ? {
                              required: true,
                              max: { length: 20 },
                              alpha_num_zenkaku: true,
                            }
                          : { max: { length: 20 }, alpha_num_zenkaku: true }
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="!isVnConnectNameSbyEnable"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- ルーティング情報(共通) -->
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(共通)</h3>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <app-label-group label="ルーティング種別" value="BGP4" />
                  </v-col>
                </v-row>
                <!--AS番号-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="AS番号"
                      :msg="
                        $filter.message(`info_as_number_aws`, {
                          numberArray: `10124、64512～64920、64922～64935、64937～64949、64973～65501、65504～65534`,
                        })
                      "
                      :is-info="isAdd"
                      :is-required="isAdd"
                    />
                    <validation-provider
                      name="asNumber"
                      label="AS番号"
                      v-model.number="
                        (form as CloudVnAwsPost).cloudVnConnectAct!.asNumber
                      "
                      :rules="{
                        required: true,
                        numeric: true,
                        is_number_range: {
                          range:
                            '10124,64512-64920,64922-64935,64937-64949,64973-65501,65504-65534',
                        },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input-number
                        v-bind="componentField"
                        :disabled="!isAdd"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--Maximum Prefix(Act)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="Maximum Prefix"
                      :is-info="false"
                      :isRequired="true"
                    />
                    <validation-provider
                      name="maximumPrefix"
                      label="Maximum Prefix(Act)"
                      v-model="form.cloudVnConnectAct!.maximumPrefix"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select
                        :items="maximumPrefixSelectList"
                        item-title="text"
                        item-value="value"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- ルーティング情報(Act) -->
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(Act)</h3>
                  </v-col>
                </v-row>
                <!--WANアドレス(Act)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="WANアドレス"
                      :msg="
                        '・' +
                        $filter.message('info_cidr_ip_v4_with_subnet_2', {
                          subnet: '30',
                        }) +
                        '\n' +
                        '・' +
                        $filter.message('info_cidr_subnet', {
                          subnet: '16-/30',
                        })
                      "
                      :is-info="isAdd"
                      :is-required="isAdd"
                    />
                    <validation-provider
                      name="wanAddressAct"
                      label="WANアドレス(Act)"
                      v-model="
                        (form as CloudVnAwsPost).cloudVnConnectAct!.wanAddress
                      "
                      :rules="{
                        required: true,
                        is_ip_subnet: { min: 16, max: 30 },
                        is_not_current_ip: true,
                        is_not_loopback_ip: true,
                        is_not_multicast_ip: true,
                        is_not_class_e_ip: true,
                        is_not_broadcast_ip: true,
                        is_not_network_ip: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="!isAdd"
                        :placeholder="
                          !isAdd
                            ? null
                            : $filter.message(
                                'placeholder_ip_address_with_subnet30'
                              )
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--ピアIPアドレス(Act)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ピアIPアドレス"
                      :msg="
                        $filter.message('info_ip_address_v4_2', { host: '2' })
                      "
                      :is-info="isAdd"
                      :is-required="isAdd"
                    />
                    <validation-provider
                      name="peerIpAddressAct"
                      label="ピアIPアドレス(Act)"
                      v-model="
                        (form as CloudVnAwsPost).cloudVnConnectAct!
                          .peerIpAddress
                      "
                      :rules="{
                        required: true,
                        is_ipv4: true,
                        cross_wan_address_within_range: {
                          wan: (form as CloudVnAwsPost).cloudVnConnectAct!
                            .wanAddress,
                        },
                        is_not_loopback_ip: true,
                        is_not_multicast_ip: true,
                        is_not_class_e_ip: true,
                        is_not_broadcast_ip: {
                          range: (form as CloudVnAwsPost).cloudVnConnectAct!
                            .wanAddress,
                        },
                        is_not_network_ip: {
                          range: (form as CloudVnAwsPost).cloudVnConnectAct!
                            .wanAddress,
                        },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="!isAdd"
                        :placeholder="
                          !isAdd
                            ? null
                            : $filter.message('placeholder_ip_address_3', {
                                host: '2',
                              })
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--Local Preference(Act)-->
                <v-row>
                  <v-col>
                    <app-label-group label="Local Preference">
                      <template #value>
                        <app-form-input
                          :value="
                            $filter.enumTo('HIGH_PRIORITY', 'localPreference')
                          "
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>
                <!--MED(Act)-->
                <v-row>
                  <v-col>
                    <app-label-group label="MED">
                      <template #value>
                        <app-form-input
                          :value="$filter.enumTo('LOW_PRIORITY', 'med')"
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>

                <!-- ルーティング情報(Sby) -->
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(Sby)</h3>
                  </v-col>
                </v-row>
                <!--WANアドレス(Sby)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="WANアドレス"
                      :msg="
                        '・' +
                        $filter.message('info_cidr_ip_v4_with_subnet_2', {
                          subnet: '30',
                        }) +
                        '\n' +
                        '・' +
                        $filter.message('info_cidr_subnet', {
                          subnet: '16-/30',
                        })
                      "
                      :is-info="!(isDisableRoutingSby || hasSby)"
                      :isRequired="!(isDisableRoutingSby || hasSby)"
                    />
                    <validation-provider
                      name="wanAddressSby"
                      label="WANアドレス(Sby)"
                      v-model="
                        (form as CloudVnAwsPost).cloudVnConnectSby!.wanAddress
                      "
                      :rules="
                        isRequireRoutingSby
                          ? {
                              required: true,
                              is_ip_subnet: { min: 16, max: 30 },
                              is_not_current_ip: true,
                              is_not_loopback_ip: true,
                              is_not_multicast_ip: true,
                              is_not_class_e_ip: true,
                              is_not_broadcast_ip: true,
                              is_not_network_ip: true,
                            }
                          : ''
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="isDisableRoutingSby || hasSby"
                        :placeholder="
                          isDisableRoutingSby || hasSby
                            ? null
                            : $filter.message(
                                'placeholder_ip_address_with_subnet30'
                              )
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--ピアIPアドレス(Sby)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ピアIPアドレス"
                      :msg="
                        $filter.message('info_ip_address_v4_2', { host: '2' })
                      "
                      :is-info="!(isDisableRoutingSby || hasSby)"
                      :isRequired="!(isDisableRoutingSby || hasSby)"
                    />
                    <validation-provider
                      name="peerIpAddressSby"
                      label="ピアIPアドレス(Sby)"
                      v-model="
                        (form as CloudVnAwsPost).cloudVnConnectSby!
                          .peerIpAddress
                      "
                      :rules="
                        isRequireRoutingSby
                          ? {
                              required: true,
                              is_ipv4: true,
                              cross_wan_address_within_range: {
                                wan: (form as CloudVnAwsPost).cloudVnConnectSby!
                                  .wanAddress,
                              },
                              is_not_loopback_ip: true,
                              is_not_multicast_ip: true,
                              is_not_class_e_ip: true,
                              is_not_broadcast_ip: {
                                range: (form as CloudVnAwsPost)
                                  .cloudVnConnectSby!.wanAddress,
                              },
                              is_not_network_ip: {
                                range: (form as CloudVnAwsPost)
                                  .cloudVnConnectSby!.wanAddress,
                              },
                            }
                          : ''
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="isDisableRoutingSby || hasSby"
                        :placeholder="
                          isDisableRoutingSby || hasSby
                            ? null
                            : $filter.message('placeholder_ip_address_3', {
                                host: '2',
                              })
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--Local Preference(Sby)-->
                <v-row>
                  <v-col>
                    <app-label-group label="Local Preference">
                      <template #value>
                        <app-form-input
                          :value="
                            $filter.enumTo('LOW_PRIORITY', 'localPreference')
                          "
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>
                <!--MED(Sby)-->
                <v-row>
                  <v-col>
                    <app-label-group label="MED">
                      <template #value>
                        <app-form-input
                          :value="$filter.enumTo('HIGH_PRIORITY', 'med')"
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>

                <!-- カスタム経路集約情報 -->
                <v-row>
                  <v-col>
                    <h3>カスタム経路集約情報</h3>
                  </v-col>
                </v-row>
                <!--経路集約-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="経路集約"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group
                      v-model="form.cloudVnConnectCommon!.routeAggregation"
                      hide-details
                      inline
                    >
                      <v-radio
                        v-for="(routeOption, index) in routeOptions"
                        :key="`${routeOption.text + index}`"
                        :label="routeOption.text"
                        :value="routeOption.value"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <!--経路集約アドレス-->
                <v-row
                  v-if="form.cloudVnConnectCommon!.routeAggregation !== 'OFF'"
                >
                  <v-col>
                    <app-badge
                      item="経路集約アドレス"
                      :is-required="
                        form.cloudVnConnectCommon!.routeAggregation === 'CUSTOM'
                      "
                      :is-info="
                        form.cloudVnConnectCommon!.routeAggregation === 'CUSTOM'
                      "
                      :msg="$filter.message('info_routing_address')"
                    />
                    <template
                      v-if="
                        form.cloudVnConnectCommon!.routeAggregation === 'ON'
                      "
                    >
                      <span class="ml-3">0.0.0.0/0</span>
                    </template>
                    <template
                      v-if="
                        form.cloudVnConnectCommon!.routeAggregation === 'CUSTOM'
                      "
                    >
                      <validation-array
                        name="routeAggregationAddressList"
                        v-slot="{ fields, push, remove }"
                      >
                        <template
                          v-for="(item, index) in fields"
                          :key="item.key"
                        >
                          <v-row class="ga-2">
                            <validation-provider
                              :name="`routingIp_${item.key}`"
                              label="経路集約アドレス"
                              v-model="
                                form.cloudVnConnectCommon!
                                  .routeAggregationAddressList![index]
                              "
                              :rules="{
                                required: true,
                                is_ip_subnet: { min: 1, max: 30 },
                                is_network_address: true,
                                is_not_default_root: true,
                                duplicate: { list: routingAddressList(index) },
                              }"
                              v-slot="{ componentField, errorMessage, meta }"
                            >
                              <app-form-input
                                class="ml-1"
                                :placeholder="
                                  $filter.message(
                                    'placeholder_ip_address_with_subnet24'
                                  )
                                "
                                v-bind="componentField"
                                :field-meta="meta"
                                :error-messages="errorMessage"
                              />
                              <v-btn
                                icon="$mdiMinus"
                                size="small"
                                variant="outlined"
                                class="rounded-sm ml-2 mr-3"
                                @click="removeRouteAddress(index, remove)"
                              />
                            </validation-provider>
                          </v-row>
                        </template>
                        <!--経路集約アドレス追加-CUSTOMのみ表示-->
                        <v-row justify="end">
                          <v-btn
                            icon="$mdiPlus"
                            size="small"
                            variant="outlined"
                            class="rounded-sm ml-2 mr-3"
                            :disabled="currentLength >= 100"
                            @click="addRouteAddress(push)"
                          />
                        </v-row>
                      </validation-array>
                    </template>
                  </v-col>
                </v-row>

                <!--備考-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :is-info="true"
                      :isRequired="false"
                    />
                    <validation-provider
                      name="description"
                      label="備考"
                      v-model="form.cloudVnConnectCommon!.description"
                      :rules="{ max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>

            <cross-validation-provider
              vid="can_not_save_act_and_create_sby"
              :passed="multipleOption"
              class="my-4"
            >
              {{ $filter.message("can_not_save_act_and_create_sby") }}
            </cross-validation-provider>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  isAdd ? "追加" : "変更"
                }}</v-btn>
                <v-btn color="primary" variant="outlined" @click="cancel"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type {
  BandWidth,
  RouteAggregation,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  VpnVnCode as CloudVpnVnCode,
} from "@/apis/CloudVnMsApi";
import type { CloudLine } from "@/apis/CloudLineApi";
import cloneDeep from "lodash/cloneDeep";
import type {
  CloudVnAwsCommon,
  CloudVnAwsActSby,
  CloudVnAwsDetail,
  CloudVnAwsPost,
  CloudVnAwsPut,
} from "@/apis/CloudVnAwsApi";
import type { VpnVnCode } from "@/apis/InformationApi";
import MultiCloudAwsVnConnectionConfirm from "@/modals/multicloud/aws/MultiCloudAwsVnConnectionConfirm.vue";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";

export default defineComponent({
  name: "MultiCloudAwsVnLineEdit",
  props: {
    /** クラウド回線SEQ */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    // 変更の場合用
    cloudVnAwsDetail: {
      type: Object as PropType<CloudVnAwsDetail>,
      default: undefined,
    },
    // 追加の場合用
    cloudVnAwsKeyId: {
      type: String as PropType<string>,
      default: undefined,
    },
    remainingBandwidth: {
      type: Number as PropType<number>,
      required: true,
    },
  },
  data() {
    // 新規/更新のformデータを作成
    const form:
      | (CloudVnAwsPost & {
          bandItem: string | null;
        })
      | (CloudVnAwsPut & {
          cloudVnConnectCommon: Pick<
            CloudVnAwsCommon,
            "directConnectType" | "location"
          >;
          bandItem: string | null;
        }) = (() => {
      if (this.cloudVnAwsDetail) {
        // 変更
        const cloudVnAwsDetailCopy = cloneDeep(this.cloudVnAwsDetail);
        // 経路集約アドレスを数値昇順でソート
        if (
          cloudVnAwsDetailCopy.cloudVnConnectCommon &&
          Array.isArray(
            cloudVnAwsDetailCopy.cloudVnConnectCommon
              .routeAggregationAddressList
          )
        ) {
          cloudVnAwsDetailCopy.cloudVnConnectCommon.routeAggregationAddressList =
            cloudVnAwsDetailCopy.cloudVnConnectCommon.routeAggregationAddressList.sortIpAddress();
        }
        const form: CloudVnAwsPut & {
          cloudVnConnectCommon: Pick<
            CloudVnAwsCommon,
            "directConnectType" | "location"
          >;
          bandItem: string | null;
        } = {
          cloudVnConnectCommon: cloudVnAwsDetailCopy.cloudVnConnectCommon,
          cloudVnConnectAct: cloudVnAwsDetailCopy.cloudVnConnectAct,
          cloudVnConnectSby: cloudVnAwsDetailCopy.cloudVnConnectSby
            ? cloudVnAwsDetailCopy.cloudVnConnectSby
            : ({
                maximumPrefix: 100,
              } as CloudVnAwsPut["cloudVnConnectSby"]),
          cloudVnAwsKeyId: cloudVnAwsDetailCopy.cloudVnAwsKeyId,
          // 変更の場合、名前でラベルを表示するので、初期値をブランクにする
          bandItem: null,
        };
        return form;
      } else {
        // 追加
        const form: CloudVnAwsPost & {
          bandItem: string | null;
        } = {
          cloudVnConnectCommon: {
            bandwidthType: "PF",
            routeAggregation: "OFF",
            routeAggregationAddressList: [""],
            location: "EAST_JAPAN",
            directConnectType: "HOSTED_VIFS",
          } as CloudVnAwsPost["cloudVnConnectCommon"],
          cloudVnConnectAct: {
            maximumPrefix: 100,
          } as CloudVnAwsPost["cloudVnConnectAct"],
          cloudVnConnectSby: {
            maximumPrefix: 100,
          } as CloudVnAwsPost["cloudVnConnectSby"],
          cloudVnAwsKeyId: this.cloudVnAwsKeyId!,
          // 追加の場合、初期値を「低帯域」にする
          bandItem: "LOW",
        };
        return form;
      }
    })();

    return {
      form: form,
      isLoaded: false,
      vpnVnCodeList: [] as VpnVnCode[],
      cloudLineMaxBandwidth: {} as CloudLine["bandwidth"],
      selectedVpnVn: null as null | CloudVpnVnCode,
      bandwidthOptions: [
        { text: "10Mbps", value: { value: 10, unit: "MBPS" } },
        { text: "20Mbps", value: { value: 20, unit: "MBPS" } },
        { text: "30Mbps", value: { value: 30, unit: "MBPS" } },
        { text: "40Mbps", value: { value: 40, unit: "MBPS" } },
        { text: "50Mbps", value: { value: 50, unit: "MBPS" } },
        { text: "60Mbps", value: { value: 60, unit: "MBPS" } },
        { text: "70Mbps", value: { value: 70, unit: "MBPS" } },
        { text: "80Mbps", value: { value: 80, unit: "MBPS" } },
        { text: "90Mbps", value: { value: 90, unit: "MBPS" } },
        { text: "100Mbps", value: { value: 100, unit: "MBPS" } },
        { text: "200Mbps", value: { value: 200, unit: "MBPS" } },
        { text: "300Mbps", value: { value: 300, unit: "MBPS" } },
        { text: "400Mbps", value: { value: 400, unit: "MBPS" } },
        { text: "500Mbps", value: { value: 500, unit: "MBPS" } },
        { text: "600Mbps", value: { value: 600, unit: "MBPS" } },
        { text: "700Mbps", value: { value: 700, unit: "MBPS" } },
        { text: "800Mbps", value: { value: 800, unit: "MBPS" } },
        { text: "900Mbps", value: { value: 900, unit: "MBPS" } },
        { text: "1Gbps", value: { value: 1, unit: "GBPS" } },
        { text: "2Gbps", value: { value: 2, unit: "GBPS" } },
        { text: "3Gbps", value: { value: 3, unit: "GBPS" } },
        { text: "4Gbps", value: { value: 4, unit: "GBPS" } },
        { text: "5Gbps", value: { value: 5, unit: "GBPS" } },
        { text: "6Gbps", value: { value: 6, unit: "GBPS" } },
        { text: "7Gbps", value: { value: 7, unit: "GBPS" } },
        { text: "8Gbps", value: { value: 8, unit: "GBPS" } },
        { text: "9Gbps", value: { value: 9, unit: "GBPS" } },
        { text: "10Gbps", value: { value: 10, unit: "GBPS" } },
      ] as { text: string; value: BandWidth }[],
      routeOptions: [
        { value: "ON", text: "ON" },
        { value: "OFF", text: "OFF" },
        { value: "CUSTOM", text: "CUSTOM" },
      ],
      locationOptions: [
        { value: "EAST_JAPAN", text: "東日本" },
        { value: "WEST_JAPAN", text: "西日本" },
      ],
      directConnectOptions: [
        { value: "HOSTED_VIFS", text: "ホスト型VIF(Private)" },
        { value: "HOSTED_CONNECTIONS", text: "ホスト接続" },
      ],
      /** Maximum Prefix上限値(変更の場合用) */
      maximumPrefixLimit: null as UpperLimitEntity | null,
      /** 1G超VNコネクト */
      vnConnects1G: null as UpperLimitEntity | undefined | null,
      /** 東日本1G超VNコネクト(ホスト型VIF) */
      eastVnConnects1G: null as UpperLimitEntity | undefined | null,
      /** 西日本1G超VNコネクト(ホスト型VIF) */
      westVnConnects1G: null as UpperLimitEntity | undefined | null,
      /** 1G超VNコネクト(ホスト型接続) */
      vnConnects1GHost: null as UpperLimitEntity | undefined | null,
      /** VNコネクト(ホスト型接続) */
      vnConnectsHost: null as UpperLimitEntity | undefined | null,
      /** AWSホスト型接続が利用可能か  */
      availableStatus: false,
      /** 設定可能帯域 */
      editRemainingBandwidth: 0 as number,
    };
  },
  computed: {
    /** VNコネクト(Act)名編集可否 */
    isVnConnectNameActEnable() {
      if (this.isAdd) {
        return true;
      }
      return !this.cloudVnAwsDetail!.cloudVnConnectAct.isVnConnectStatus;
    },
    /** VNコネクト(Sby)名編集可否 */
    isVnConnectNameSbyEnable() {
      // 追加あるいはSby自体が存在しない場合
      if (this.isAdd || !this.cloudVnAwsDetail!.cloudVnConnectSby) {
        return true;
      }
      return !this.cloudVnAwsDetail!.cloudVnConnectSby.isVnConnectStatus;
    },

    /** 相関チェック
     * false: Sbyの登録とAct、Commonの編集は同時に行う場合 */
    multipleOption(): boolean {
      if (
        this.isAdd ||
        this.cloudVnAwsDetail!.cloudVnConnectSby ||
        !this.form.cloudVnConnectSby?.vnConnectName
      ) {
        return true;
      }
      let changed = this.$crossValidation.objectChanged(
        this.cloudVnAwsDetail!.cloudVnConnectCommon,
        this.form.cloudVnConnectCommon!
      );
      if (!changed) {
        changed = this.$crossValidation.objectChanged(
          this.cloudVnAwsDetail!.cloudVnConnectAct,
          this.form.cloudVnConnectAct!
        );
      }
      return !changed;
    },
    currentLength(): number {
      return this.form.cloudVnConnectCommon!.routeAggregationAddressList
        ? this.form.cloudVnConnectCommon!.routeAggregationAddressList.length
        : 0;
    },
    /** true: 新規, false: 変更 */
    isAdd(): boolean {
      return !this.cloudVnAwsDetail;
    },
    /** 品目(帯域)ラジオボタン表示 */
    isBandwidthItemShow(): boolean {
      // 「接続サービス」=「ホスト型VIF(Private)」、「ロケーション」=「東日本」、「東日本1G超VNコネクト(ホスト型VIF)」が存在
      // 「東日本1G超VNコネクト(ホスト型VIF)」が存在しない場合、「低帯域/広帯域」を表示しないように
      return (
        this.form.cloudVnConnectCommon!.directConnectType === "HOSTED_VIFS" &&
        this.form.cloudVnConnectCommon!.location === "EAST_JAPAN" &&
        this.eastVnConnects1G != null
      );
    },
    /** 品目(帯域)の「広帯域」選択肢の非活性化 */
    highBandwidthDisable(): boolean {
      // 「東日本1G超VNコネクト(ホスト型VIF)」が存在して、かつ、上限値が1Gbps以下の場合
      const bandwitdhBps =
        this.$filter.bandwidthToBps({
          value: this.eastVnConnects1G ? this.eastVnConnects1G.upperLimit : 10,
          unit: "GBPS",
        }) ?? 0;
      // クラウド帯域値
      const limit1 = this.$filter.bandwidthToBps(this.cloudLineMaxBandwidth);
      return (
        bandwitdhBps <= 1000000000 ||
        limit1! <= 1000000000 ||
        this.remainingBandwidth <= 1000000000
      );
    },
    /** 品目(帯域)ラベル表示名 */
    bandItemName(): string | null {
      if (!this.isAdd && this.isBandwidthItemShow) {
        const bandwidth =
          this.$filter.bandwidthToBps(
            this.form.cloudVnConnectCommon!.bandwidth
          ) ?? 0;
        // 1Gbps以下の場合、「低帯域」ラベルで表示する
        if (bandwidth <= 1000000000) {
          return "低帯域";
        } else {
          // 上記以外の場合、「広帯域」ラベルで表示する
          return "広帯域";
        }
      }
      return null;
    },
    vpnVnCodeSelectList(): {
      value: string;
      text: string;
    }[] {
      return this.vpnVnCodeList.map((v) => {
        if (v.vnName) {
          return {
            value: v.vpnVnCode,
            text: v.vnName + ":" + v.vpnVnCode + "(L3)",
          };
        } else {
          return { value: v.vpnVnCode, text: v.vpnVnCode };
        }
      });
    },
    /** 接続サービスの選択肢 */
    directConnectTypeSelectList(): {
      text: string;
      value: string;
    }[] {
      // クラウド帯域値
      const limit1 = this.$filter.bandwidthToBps(this.cloudLineMaxBandwidth);
      // 50Mbps未満の場合（50Mbpsは含まない）
      if (limit1! < 50000000) {
        // 「ホスト型VIF(Private)」しか表示されない
        return this.directConnectOptions.filter(
          (v) => v.value === "HOSTED_VIFS"
        );
      }
      return this.directConnectOptions;
    },
    /** 品目の選択肢 */
    bandwidthSelectList(): { text: string; value: BandWidth }[] {
      // クラウド帯域値
      const limit1 = this.$filter.bandwidthToBps(this.cloudLineMaxBandwidth);

      // 設定上限値
      let limit2_value = 0;
      // ホスト型VIF(Private)
      if (this.form.cloudVnConnectCommon?.directConnectType === "HOSTED_VIFS") {
        // 「1G超VNコネクト」
        //  存在しない場合、制限なしで、default値を1Gbpsにする
        limit2_value = this.vnConnects1G ? this.vnConnects1G.upperLimit : 1;
        if (
          this.form.cloudVnConnectCommon?.location === "EAST_JAPAN" &&
          this.eastVnConnects1G
        ) {
          // 東日本 かつ 「東日本1G超VNコネクト(ホスト型VIF)」が存在する場合
          limit2_value = this.eastVnConnects1G!.upperLimit;
        } else if (
          this.form.cloudVnConnectCommon?.location === "WEST_JAPAN" &&
          this.westVnConnects1G
        ) {
          // 西日本 かつ 「西日本1G超VNコネクト(ホスト型VIF)」が存在する場合
          limit2_value = this.westVnConnects1G!.upperLimit;
        }
      } else {
        // 「ホスト型接続」の場合
        // 「1G超VNコネクト(ホスト型接続)」
        limit2_value = this.vnConnects1GHost
          ? this.vnConnects1GHost.upperLimit
          : 1;
        if (this.vnConnectsHost) {
          // 「VNコネクト(ホスト型接続)」が存在する場合、「VNコネクト(ホスト型接続)」にする
          limit2_value = this.vnConnectsHost!.upperLimit;
        }
      }
      // 帯域ObjectをBPS単位に変換する
      const limit2 = this.$filter.bandwidthToBps({
        value: limit2_value,
        unit:
          this.form.cloudVnConnectCommon?.directConnectType === "HOSTED_VIFS"
            ? "GBPS"
            : this.vnConnects1GHost
              ? "GBPS"
              : "MBPS",
      });

      // 品目(帯域)ラジオボタンに「低帯域」選択場合の上限値：1G
      let limit3 = 10000000000;
      // 広帯域の最小帯域
      let upper = 0;
      // 品目(帯域)ラジオボタンが表示される場合、選択肢より、品目プルダウン表示内容も変わる
      if (this.isBandwidthItemShow) {
        if (this.form.bandItem === "LOW" || this.bandItemName === "低帯域") {
          // 1Gbps
          limit3 = 1000000000;
        } else {
          // 2Gbps
          upper = 2000000000;
        }
      }

      // 選択肢内容を処理する
      const bandwidthOptions = this.bandwidthOptions.filter((v) => {
        const bps = this.$filter.bandwidthToBps(v.value)!;
        return (
          bps <= limit1! &&
          bps <= limit2! &&
          bps <= limit3! &&
          bps <= this.editRemainingBandwidth! &&
          bps >= upper
        );
      });

      return this.form.cloudVnConnectCommon!.directConnectType === "HOSTED_VIFS"
        ? bandwidthOptions
        : bandwidthOptions.filter(
            (v) =>
              (v.value.unit === "MBPS" &&
                [50, 100, 200, 300, 400, 500].includes(v.value.value)) ||
              (v.value.unit === "GBPS" && [1, 2, 5, 10].includes(v.value.value))
          );
    },
    /** Maximum Prefixの選択肢 */
    maximumPrefixSelectList(): { text: string; value: number }[] {
      if (this.isAdd) {
        return [{ value: 100, text: "100" }];
      } else {
        return [
          { value: 100, text: "100" },
          { value: 200, text: "200" },
          { value: 300, text: "300" },
          { value: 400, text: "400" },
          { value: 500, text: "500" },
          { value: 1000, text: "1000" },
          { value: 1500, text: "1500" },
          { value: 2000, text: "2000" },
        ].filter((v) => v.value <= this.maximumPrefixLimit!.upperLimit);
      }
    },

    vpnVnName(): string {
      if (
        this.cloudVnAwsDetail!.cloudVnConnectCommon.vpnVnCode.startsWith("D")
      ) {
        return (
          this.cloudVnAwsDetail!.cloudVnConnectCommon.vnName +
          ":" +
          this.cloudVnAwsDetail!.cloudVnConnectCommon.vpnVnCode +
          "(L3)"
        );
      } else {
        return this.cloudVnAwsDetail!.cloudVnConnectCommon.vpnVnCode;
      }
    },
    /** true: Sbyに関する項目は非活性にする */
    isDisableRoutingSby(): boolean {
      return !(
        (this.cloudVnAwsDetail && this.cloudVnAwsDetail.cloudVnConnectSby) ||
        this.form.cloudVnConnectSby?.vnConnectName
      );
    },
    /** true: Sbyに関する項目はValidation必要 */
    isRequireRoutingSby(): boolean {
      // Sby情報はdisabledの場合、false
      return !this.isDisableRoutingSby;
    },
    /** true:回線詳細にSby情報が登録済みの場合 */
    hasSby(): boolean {
      return !!(
        this.cloudVnAwsDetail && this.cloudVnAwsDetail.cloudVnConnectSby
      );
    },
    /** 経路集約アドレスの重複チェック用リスト作成 */
    routingAddressList() {
      return (index: number) => {
        return this.form.cloudVnConnectCommon!.routeAggregationAddressList
          ? this.form.cloudVnConnectCommon!.routeAggregationAddressList.filter(
              (_, num) => num !== index
            )
          : [];
      };
    },
    /** 変更モード、ホスト型接続の判断. */
    isHostedConnections(): boolean {
      return (
        !this.isAdd &&
        this.form.cloudVnConnectCommon!.directConnectType ===
          `HOSTED_CONNECTIONS`
      );
    },
    /** 接続サービスによって品目で表示するinfo文言を変更 */
    itemMessage() {
      switch (this.form.cloudVnConnectCommon!.directConnectType) {
        case "HOSTED_VIFS":
          return `・品目は「低帯域（10Mbps～1Gbps）」または「広帯域（2Gbps～10Gbps）」からご選択ください。
          ・低帯域⇔広帯域の変更はできません。既存VNコネクトの削除後に新規作成の手続きが必要です。`;
        case "HOSTED_CONNECTIONS":
          return "品目の変更は出来ません。既存VNコネクトの削除後に新規作成の手続きが必要です。";
        default:
          return "";
      }
    },
  },
  watch: {
    /** 経路集約がCUSTOMに変更されて初期値が存在しない場合は初期化 */
    "form.cloudVnConnectCommon.routeAggregation"(newValue: RouteAggregation) {
      if (
        newValue === "CUSTOM" &&
        (!this.form.cloudVnConnectCommon!.routeAggregationAddressList ||
          this.form.cloudVnConnectCommon!.routeAggregationAddressList.length ===
            0)
      ) {
        this.form.cloudVnConnectCommon!.routeAggregationAddressList = [""];
      }
    },
    /** 接続サービスの値が変更された場合、品目の値を初期化 */
    "form.cloudVnConnectCommon.directConnectType"() {
      if (this.form.cloudVnConnectCommon!.bandwidth) {
        this.form.cloudVnConnectCommon!.bandwidth = { value: 0, unit: "MBPS" };
      }
    },
    /** 帯域が変更されましたら、既存値をクリアする */
    "form.bandItem"() {
      if (this.form.cloudVnConnectCommon!.bandwidth) {
        this.form.cloudVnConnectCommon!.bandwidth = { value: 0, unit: "MBPS" };
      }
    },
  },
  async mounted() {
    this.maxBandwidth();
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      // 回線情報に紐づくクラウド回線情報を取得APIを呼び出す
      const lines = (await this.$api.cloudLine.getCloudLines()).lineList;
      const cloudLines = lines.flatMap(
        (v: { cloudLineList: any }) => v.cloudLineList
      );
      // クラウド回線の帯域最大値(品目の上限値制御用)
      this.cloudLineMaxBandwidth = cloudLines.find(
        (v: { cloudLineSeq: string }) => v.cloudLineSeq === this.cloudLineSeq
      )!.bandwidth;

      // 1G超VNコネクトの値を取得（品目の上限値制御用）
      const getVnConnects1G = await this.$api.upperLimit.getUpperLimitLine({
        upperLimitLineManageUnit: "MULTI_CLOUD",
        cloudLineSeq: this.cloudLineSeq,
      });
      this.vnConnects1G = getVnConnects1G.cloudLine!.upperLimitList!.find(
        (v: { upperLimitItemName: string }) =>
          v.upperLimitItemName === "1G超VNコネクト"
      );
      this.eastVnConnects1G = getVnConnects1G.cloudLine!.upperLimitList!.find(
        (v: { upperLimitItemName: string }) =>
          v.upperLimitItemName === "東日本1G超VNコネクト(ホスト型VIF)"
      );
      this.westVnConnects1G = getVnConnects1G.cloudLine!.upperLimitList!.find(
        (v: { upperLimitItemName: string }) =>
          v.upperLimitItemName === "西日本1G超VNコネクト(ホスト型VIF)"
      );
      this.vnConnects1GHost = getVnConnects1G.cloudLine!.upperLimitList!.find(
        (v: { upperLimitItemName: string }) =>
          v.upperLimitItemName === "1G超VNコネクト(ホスト型接続)"
      );
      this.vnConnectsHost = getVnConnects1G.cloudLine!.upperLimitList!.find(
        (v: { upperLimitItemName: string }) =>
          v.upperLimitItemName === "VNコネクト(ホスト型接続)"
      );

      // 設定上限値情報のチェック
      if (
        (!this.vnConnects1GHost && !this.vnConnectsHost) ||
        (this.vnConnects1GHost && this.vnConnectsHost)
      ) {
        // 「1G超VNコネクト(ホスト型接続)」「VNコネクト(ホスト型接続)」が両方存在しない又は両方存在する：システムエラー
        throw new Error(
          "Only one of the upper bounds must exist.. Items:1G超VNコネクト(ホスト型接続),VNコネクト(ホスト型接続)"
        );
      } else if (
        (!this.vnConnects1G && !this.eastVnConnects1G) ||
        (this.vnConnects1G && this.eastVnConnects1G)
      ) {
        // 「1G超VNコネクト」「東日本1G超VNコネクト(ホスト型VIF)」が両方存在しない又は両方存在する：システムエラー
        throw new Error(
          "Only one of the upper bounds must exist.. Items: 1G超VNコネクト,東日本1G超VNコネクト(ホスト型VIF)"
        );
      } else if (
        (!this.vnConnects1G && !this.westVnConnects1G) ||
        (this.vnConnects1G && this.westVnConnects1G)
      ) {
        // 「1G超VNコネクト」「西日本1G超VNコネクト(ホスト型VIF)」が両方存在しない又は両方存在する：システムエラー
        throw new Error(
          "Only one of the upper bounds must exist.. Items: 1G超VNコネクト,西日本1G超VNコネクト(ホスト型VIF)"
        );
      }

      if (this.isAdd) {
        this.vpnVnCodeList = (await this.$api.information.getVpnVnCodeList())
          .filter(
            (v: { vnType: string | null }) =>
              v.vnType === "L3" || v.vnType === null
          )
          .sortBy(["vpnVnCode", "asc"]);
        // 追加の場合、AWSホスト型接続が利用可能かを取得
        this.availableStatus = (
          await this.$api.cloudVnAws.getAwsHostConnectAvailable()
        ).availableStatus;
      } else {
        // maximumPrefixの上限値を取得
        this.maximumPrefixLimit = (
          await this.$api.upperLimit.getUpperLimitLine({
            upperLimitLineManageUnit: "MULTI_CLOUD",
            cloudLineSeq: this.cloudLineSeq,
            wnumber: this.cloudVnAwsDetail!.cloudVnConnectAct.wnumber,
          })
        ).cloudLine!.upperLimitList!.find(
          (v: { upperLimitItemName: string }) =>
            v.upperLimitItemName === "BGP4の最大経路数"
        )!;
      }
    },

    /** 設定可能帯域の算出 */
    maxBandwidth() {
      if (!this.isAdd) {
        // 編集の場合、設定可能帯域に現在の設定値を加算
        this.editRemainingBandwidth =
          this.remainingBandwidth +
          this.$filter.bandwidthToBps(
            this.cloudVnAwsDetail!.cloudVnConnectCommon.bandwidth
          )!;
      } else {
        // 追加の場合、設定可能帯域をそのまま利用
        this.editRemainingBandwidth = this.remainingBandwidth;
      }
      return this.editRemainingBandwidth;
    },

    /** 経路集約アドレス削除 */
    removeRouteAddress(index: number, remove: (idx: number) => void) {
      const list = this.form.cloudVnConnectCommon!.routeAggregationAddressList!;
      if (list.length > 1) {
        list.splice(index, 1);
        // ValidationにFieldを削除することを伝える
        remove(index);
      } else {
        this.form.cloudVnConnectCommon!.routeAggregationAddressList = [""];
      }
    },

    /** 経路集約アドレス追加 */
    addRouteAddress(push: (item: any) => void) {
      const addItem = "";
      this.form.cloudVnConnectCommon!.routeAggregationAddressList = [
        ...this.form.cloudVnConnectCommon!.routeAggregationAddressList!,
        addItem,
      ];
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },
    async submit() {
      const form = {
        ...this.form,
        cloudVnConnectCommon: {
          ...this.form.cloudVnConnectCommon,
          routeAggregationAddressList:
            this.form.cloudVnConnectCommon!.routeAggregation === "ON"
              ? []
              : this.form.cloudVnConnectCommon!.routeAggregation === "OFF"
                ? null
                : this.form.cloudVnConnectCommon!.routeAggregationAddressList,
        },
      } as
        | (CloudVnAwsPost & {
            bandItem: string | null;
          })
        | (CloudVnAwsPut & {
            cloudVnConnectSby: Pick<CloudVnAwsActSby, "asNumber">;
            cloudVnConnectAct: Pick<CloudVnAwsActSby, "asNumber">;
            bandItem: string | null;
          });
      //AS番号(act)の値をAS番号(sby)に代入する
      form.cloudVnConnectSby!.asNumber = form.cloudVnConnectAct!.asNumber;
      //Maximum Prefix(Act)の値をMaximum Prefix(Sby)に代入する
      form.cloudVnConnectSby!.maximumPrefix =
        form.cloudVnConnectAct!.maximumPrefix;
      if (this.isAdd) {
        // 追加の場合
        // 確認モーダルの表示
        let displayVnName;
        const vnSbyName = form.cloudVnConnectSby?.vnConnectName;
        (form as CloudVnAwsPost).cloudVnConnectCommon!.vpnVnCode =
          this.selectedVpnVn!;
        displayVnName = this.vpnVnCodeList.find(
          (v) =>
            v.vpnVnCode ===
            (form as CloudVnAwsPost).cloudVnConnectCommon!.vpnVnCode
        )!.vnName;
        const bandwidthDisplayName =
          this.isBandwidthItemShow && form.bandItem
            ? form.bandItem === "LOW"
              ? "低帯域"
              : "広帯域"
            : null;
        await this.$modal.show(MultiCloudAwsVnConnectionConfirm, {
          cloudVnAwsDetail: {
            cloudVnConnectCommon: {
              ...form.cloudVnConnectCommon,
              vnName: displayVnName,
              routingType: "BGP4",
            },
            cloudVnConnectAct: {
              ...form.cloudVnConnectAct,
              localPreference: "HIGH_PRIORITY",
              med: "LOW_PRIORITY",
            },
            cloudVnConnectSby: vnSbyName
              ? {
                  ...form.cloudVnConnectSby,
                  localPreference: "LOW_PRIORITY",
                  med: "HIGH_PRIORITY",
                }
              : null,
          },
          displayType: "A",
          bandwidthDisplayName: bandwidthDisplayName,
        });
        await this.$api.cloudVnAws.postVnConnect(
          this.cloudLineSeq,
          (form.cloudVnConnectSby?.vnConnectName
            ? form
            : { ...form, cloudVnConnectSby: null }) as CloudVnAwsPost
        );
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
      } else {
        if (
          !this.cloudVnAwsDetail!.cloudVnConnectSby?.vnConnectName &&
          form.cloudVnConnectSby?.vnConnectName
        ) {
          // 確認モーダルの表示
          await this.$modal.show(MultiCloudAwsVnConnectionConfirm, {
            cloudVnAwsDetail: {
              cloudVnConnectCommon: form.cloudVnConnectCommon,
              cloudVnConnectAct: form.cloudVnConnectAct,
              cloudVnConnectSby: {
                ...form.cloudVnConnectSby,
                localPreference: "LOW_PRIORITY",
                med: "HIGH_PRIORITY",
              },
            },
            displayType: "M",
            bandwidthDisplayName: this.bandItemName,
          });
          // Sbyのみ追加の場合
          await this.$api.cloudVnAws.postVnConnect(this.cloudLineSeq, {
            cloudVnConnectCommon: null,
            cloudVnConnectAct: null,
            cloudVnConnectSby: {
              ...form.cloudVnConnectSby,
              pairCloudVnConnectSeq:
                this.cloudVnAwsDetail!.cloudVnConnectAct.cloudVnConnectSeq,
            },
            cloudVnAwsKeyId: this.cloudVnAwsDetail!.cloudVnAwsKeyId,
          } as CloudVnAwsPost);
        } else {
          // 確認モーダルの表示
          const vnSbyName = form.cloudVnConnectSby?.vnConnectName;
          await this.$modal.show(MultiCloudAwsVnConnectionConfirm, {
            cloudVnAwsDetail: {
              ...form,
              cloudVnConnectSby: vnSbyName ? form.cloudVnConnectSby : null,
            },
            displayType: "M",
            bandwidthDisplayName: this.bandItemName,
          });
          // 変更のみの場合
          await this.$api.cloudVnAws.putVnConnect(
            this.cloudLineSeq,
            this.cloudVnAwsDetail!.cloudVnConnectAct.wnumber,
            (this.cloudVnAwsDetail!.cloudVnConnectSby?.vnConnectName
              ? form
              : { ...form, cloudVnConnectSby: null }) as CloudVnAwsPut
          );
        }

        await (
          this.$refs.modal as typeof VueFinalModal & { ok: () => void }
        ).ok();
      }
    },
  },
});
</script>
